/* ONE ATLAS INSTEAD OF A THOUSAND CANVASES (LS-235).
 *
 * Vijay, from a desktop console after a while: "CONTEXT_LOST_WEBGL: loseContext:
 * context lost". Measured over two minutes of walking, the game made 13,195
 * canvases -- one per creature sprite, one per body's normal map, one per lit
 * crown -- and its caches held thousands of them. A canvas that is drawn
 * from is a texture in the GPU process, and a process that holds thousands
 * of textures is a process whose WebGL context the browser reclaims.
 *
 * So the small pictures share a few big ones. An atlas is a 1024-square
 * canvas cut into shelves: a picture asks for a cell of its size, is written
 * there once, and is drawn from there with the nine-argument drawImage. When
 * the atlases are full (eight of them, sixty-four megapixels of cells) every
 * cache that packed into them is told to forget, and the shelves start again.
 */
const SIZE = 1024;
const MAX_ATLASES = 8;
const atlases = [];   // { canvas, g, shelves: [{ y, h, x }], top }
const forgetters = [];
export let atlasEpoch = 0;

/** Register a cache that packs into the atlases, so a reset can empty it. */
export function onAtlasReset(fn) { forgetters.push(fn); }

function fresh() {
  const canvas = document.createElement('canvas'); canvas.width = SIZE; canvas.height = SIZE;
  return { canvas, g: canvas.getContext('2d'), shelves: [], top: 0 };
}

function allocIn(a, w, h) {
  // a shelf of the same height (rounded up to 8) with room, else a new shelf
  const sh = (h + 7) & ~7;
  for (const s of a.shelves) if (s.h === sh && s.x + w <= SIZE) { const at = { sx: s.x, sy: s.y }; s.x += w; return at; }
  if (a.top + sh > SIZE) return null;
  const s = { y: a.top, h: sh, x: w }; a.shelves.push(s); a.top += sh;
  return { sx: 0, sy: s.y };
}

/** Allocate a sprite cell. beforeReset drains queued readers before their pixels are reused. */
export function atlasCell(w, h, beforeReset: (() => void) | null = null) {
  if (typeof document === 'undefined') { const canvas = { width: w, height: h, getContext: () => null }; return { canvas, g: null, sx: 0, sy: 0 }; }
  if (w > SIZE || h > SIZE) { const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h; return { canvas, g: canvas.getContext('2d'), sx: 0, sy: 0 }; }
  for (const a of atlases) { const at = allocIn(a, w, h); if (at) return { canvas: a.canvas, g: a.g, ...at }; }
  if (atlases.length >= MAX_ATLASES) {
    beforeReset?.();
    // full: every cache that packed here forgets, and the shelves start again
    for (const f of forgetters) { try { f(); } catch {} }
    for (const a of atlases) { a.shelves.length = 0; a.top = 0; a.g.clearRect(0, 0, SIZE, SIZE); }
    atlasEpoch++;
  } else atlases.push(fresh());
  const a = atlases[atlases.length - 1]; const at = allocIn(a, w, h);
  return { canvas: a.canvas, g: a.g, ...at };
}

export function atlasCount() { return atlases.length; }
